import axios from "axios"
import { showToast } from "../../shared/utils/toast"
const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

if (token) {
  axios.defaults.headers.common["X-CSRF-Token"] = token
}

export const ProductVariant = () => ({
  selectedType: 0,
  quantity: 1,
  selected: {},
  selectedOptions: {},
  defaults: {},
  variants: [],
  sale_price: 0,
  regular_price: 0,
  product_image_id: null,
  isReadyToBuy: false,
  productId: null,
  salePriceDisplay: '',
  regularPriceDisplay: '',
  availableOptionValues: {},

  init() {
    try {
      this.defaults = JSON.parse(this.$el.dataset.defaults || "{}")
      this.variants = JSON.parse(this.$el.dataset.variants || "[]")

      this.productId = this.defaults.product
      this.sale_price = this.defaults.sale_price || 0
      this.regular_price = this.defaults.regular_price || 0
      this.salePriceDisplay = this.defaults.formatted_sale_price
      this.regularPriceDisplay = this.defaults.formatted_regular_price

      this.onSelectionChange();

      this.$watch('selectedOptions', () => this.onSelectionChange(), { deep: true });
      
    } catch (error) {
      console.error("Error initializing product variant selector:", error);
      this.defaults = { sale_price: 0, regular_price: 0, stock: null }
      this.variants = []
      this.isReadyToBuy = true
      this.salePriceDisplay = this.defaults.formatted_sale_price
      this.regularPriceDisplay = this.defaults.formatted_regular_price
    }
  },

  // 電腦版的取消選取
  updateSelection(optionId, valueId) {
    if (this.selectedOptions[optionId] === valueId) {
      delete this.selectedOptions[optionId];
    } else {
      this.selectedOptions[optionId] = valueId;
    }
  },

  onSelectionChange() {
    const selectedEntries = this._normalizeSelectedOptions();
    const match = this._findMatchingVariant(selectedEntries);

    this.quantity = 1;

    if (match) {
      this.sale_price = match.sale_price
      this.regular_price = match.regular_price
      this.salePriceDisplay = match.formatted_sale_price
      this.regularPriceDisplay = match.formatted_regular_price

      if (match.product_image_id) {
        this.updateProductImage(match.product_image_id)
      }
    } else {
      this.sale_price = this.defaults.sale_price;
      this.regular_price = this.defaults.regular_price
      this.salePriceDisplay = this.defaults.formatted_sale_price
      this.regularPriceDisplay = this.defaults.formatted_regular_price
    }

    this.isReadyToBuy = this._calculateIsReadyToBuy()
    this.updateAvailableOptions()
  },

  updateAvailableOptions() {
    const allOptionTypes = [...new Set(this.variants.flatMap(v => v.options.map(o => o.option)))];
    let newAvailable = {};
    const selectedCount = Object.keys(this.selectedOptions).length;

    allOptionTypes.forEach(optionId => {
        const otherSelections = { ...this.selectedOptions };
        if (this.selectedOptions[optionId]) {
            delete otherSelections[optionId];
        }

        const possibleVariants = this.variants.filter(variant => {
            if (variant.stock !== null && variant.stock <= 0) return false;
            return Object.entries(otherSelections).every(([optId, valId]) =>
                variant.options.some(o => o.option == optId && o.value == valId)
            );
        });

        newAvailable[optionId] = [...new Set(possibleVariants
            .flatMap(v => v.options)
            .filter(ov => ov.option === optionId)
            .map(ov => String(ov.value)))];
    });

    if (selectedCount === 0) {
        const inStockValues = this.variants
            .filter(v => v.stock === null || v.stock > 0)
            .flatMap(v => v.options);

        allOptionTypes.forEach(optionId => {
            newAvailable[optionId] = [...new Set(inStockValues
                .filter(ov => ov.option === optionId)
                .map(ov => String(ov.value)))];
        });
    }

    this.availableOptionValues = newAvailable;
  },

  isOptionValueDisabled(optionId, valueId) {
      if (this.availableOptionValues[optionId]) {
          return !this.availableOptionValues[optionId].includes(String(valueId));
      }
      if (Object.keys(this.selectedOptions).length === 0) {
          return false;
      }
      return false;
  },

  _calculateIsReadyToBuy() {
    const allRequiredOptionIds =
      this.variants.length > 0 ? [...new Set(this.variants.flatMap((v) => v.options.map((o) => o.option)))] : []

    return (
      allRequiredOptionIds.length === 0 ||
      allRequiredOptionIds.every((optionId) => this.selectedOptions.hasOwnProperty(optionId))
    )
  },

  isSelected(optionId, valueId) {
    return this.selectedOptions[optionId] == valueId
  },

  optionBtnClass(optionId, valueId) {
    return [
      "items-center",
      "px-2.5",
      "py-1.5",
      "border",
      "border-gray-400",
      "rounded-lg",
      "cursor-pointer",
      "transition",
      "text-gray-500",
      this.isSelected(optionId, valueId) ? "bg-purple-200 text-purple-800" : "bg-white",
    ]
  },

  pickVariant(id) {
    this.selectedType = id
  },

  incrementQuantity() {
    const maxStock = this.calcStock

    if (maxStock == null || this.quantity < maxStock) {
      this.quantity++
    }
  },

  decrementQuantity() {
    if (this.quantity > 1) {
      this.quantity--
    }
  },

  get calcStock() {
    const match = this._findMatchingVariantBySelected()
    return match ? match.stock : this.defaults.stock || null
  },

  updateProductImage(imageId) {
    if (!imageId || imageId == this.product_image_id) return

    this.product_image_id = imageId
    this.$dispatch("variant-image-change", { imageId })
  },

  _normalizeSelectedOptions() {
    return Object.entries(this.selectedOptions).map(([k, v]) => ({
      option: Number(k),
      value: Number(v),
    }))
  },

  _findMatchingVariant(selectedEntries) {
    return this.variants.find((variant) => {
      if (variant.options.length !== selectedEntries.length) return false

      return selectedEntries.every((sel) =>
        variant.options.some((opt) => opt.option == sel.option && opt.value == sel.value),
      )
    })
  },

  _findMatchingVariantBySelected() {
    if (Object.keys(this.selectedOptions).length == 0) return null

    const selectedEntries = this._normalizeSelectedOptions()
    return this._findMatchingVariant(selectedEntries)
  },

  async addToCart() {
    if (!this.isReadyToBuy) return;

    try {
      const res = await axios.get("/api/v1/auth/check_login")

      if (res.data.logged_in == true) {
        const { data } = await axios.post("/api/v1/cart/add_item", {
          cart_item: {
            product_id: this.productId,
            options: this._normalizeSelectedOptions(),
            quantity: this.quantity,
          },
        })

        this.$store.frontend.cartItemCount = data.cart.items.length
        this.$store.frontend.cart = data.cart

        showToast("已加入購物車", "success")
      } else {
        const cartItemParams = {
          product_id: this.productId,
          quantity: this.quantity,
          options: this._normalizeSelectedOptions()
        }
        
        const params = new URLSearchParams({
          intent: 'add_to_cart',
          'cart_item[product_id]': cartItemParams.product_id,
          'cart_item[quantity]': cartItemParams.quantity
        })
        
        cartItemParams.options.forEach((opt, index) => {
          params.append(`cart_item[options][${index}][option]`, opt.option)
          params.append(`cart_item[options][${index}][value]`, opt.value)
        })
        
        window.location.href = `/session/new?${params.toString()}`
      }
    } catch (error) {
      alert("加入購物車失敗，請稍後再試")
      console.error(error)
    }
  },

  async addToCartAndBuyNow() {
    if (!this.isReadyToBuy) return;

    try {
      const res = await axios.get("/api/v1/auth/check_login")

      if (res.data.logged_in == true) {
        const { data } = await axios.post("/api/v1/cart/add_item", {
          cart_item: {
            product_id: this.productId,
            options: this._normalizeSelectedOptions(),
            quantity: this.quantity,
          },
        })

        this.$store.frontend.cartItemCount = data.cart.items.length
        this.$store.frontend.cart = data.cart

        window.location.href = "/cart"
      } else {
        const cartItemParams = {
          product_id: this.productId,
          quantity: this.quantity,
          options: this._normalizeSelectedOptions()
        }
        
        const params = new URLSearchParams({
          intent: 'buy_now',
          'cart_item[product_id]': cartItemParams.product_id,
          'cart_item[quantity]': cartItemParams.quantity
        })
        
        cartItemParams.options.forEach((opt, index) => {
          params.append(`cart_item[options][${index}][option]`, opt.option)
          params.append(`cart_item[options][${index}][value]`, opt.value)
        })
        
        window.location.href = `/session/new?${params.toString()}`
      }
    } catch (error) {
      alert("操作失敗，請稍後再試")
      console.error(error)
    }
  },
})
